/**
 * N-Back content: types, local stimulus bank, and pool resolver (backend-first, local fallback).
 */

export interface NBackStimulusItem {
  id: string;
  label: string;
  imageUrl: string;
  category?: string;
}

/** Backend can return items in a flat array or grouped by category */
export interface NBackContentResponse {
  items?: NBackStimulusItem[];
  categories?: Record<string, NBackStimulusItem[]>;
}

/** Backend item may use itemId instead of id */
function hasValidItemId(item: Record<string, unknown>): boolean {
  const id = item?.id ?? item?.itemId;
  return typeof id === "string";
}

function isNBackContentResponse(
  data: unknown
): data is NBackContentResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  if (Array.isArray(d.items) && d.items.length > 0) {
    const first = d.items[0] as Record<string, unknown>;
    return (
      hasValidItemId(first) &&
      (typeof first?.label === "string" || first?.label == null) &&
      (typeof first?.imageUrl === "string" || first?.imageUrl == null)
    );
  }
  if (d.categories && typeof d.categories === "object") {
    const cats = d.categories as Record<string, unknown>;
    const firstKey = Object.keys(cats)[0];
    const arr = firstKey ? (cats[firstKey] as unknown[]) : null;
    if (Array.isArray(arr) && arr.length > 0) {
      const first = arr[0] as Record<string, unknown>;
      return hasValidItemId(first);
    }
  }
  return false;
}

/** Map backend item (itemId, categories[]) to NBackStimulusItem */
function mapBackendItemToStimulus(x: Record<string, unknown>): NBackStimulusItem {
  const id = (x.id ?? x.itemId) as string;
  const categories = x.categories;
  const category = Array.isArray(categories) && categories.length > 0
    ? (categories[0] as string)
    : (x.category as string | undefined);
  return {
    id,
    label: (x.label as string) ?? "",
    imageUrl: (x.imageUrl as string) ?? "",
    category,
  };
}

/** Normalize backend response to flat array of stimuli */
export function normalizeNBackContent(data: NBackContentResponse): NBackStimulusItem[] {
  const raw = data as Record<string, unknown>;
  if (Array.isArray(raw.items) && raw.items.length > 0) {
    return raw.items.map((x) => mapBackendItemToStimulus(x as Record<string, unknown>));
  }
  if (raw.categories && typeof raw.categories === "object") {
    const out: NBackStimulusItem[] = [];
    for (const arr of Object.values(raw.categories)) {
      if (Array.isArray(arr)) {
        for (const x of arr) {
          out.push(mapBackendItemToStimulus(x as Record<string, unknown>));
        }
      }
    }
    return out;
  }
  return [];
}

/** Local fallback: minimal placeholder stimuli per category (spec: animals, vehicles, food, household, nature, faces) */
const LOCAL_CATEGORIES: Record<string, NBackStimulusItem[]> = {
  animals: [
    { id: "cat", label: "Cat", imageUrl: "", category: "animals" },
    { id: "dog", label: "Dog", imageUrl: "", category: "animals" },
    { id: "bird", label: "Bird", imageUrl: "", category: "animals" },
    { id: "fish", label: "Fish", imageUrl: "", category: "animals" },
    { id: "rabbit", label: "Rabbit", imageUrl: "", category: "animals" },
    { id: "horse", label: "Horse", imageUrl: "", category: "animals" },
    { id: "mouse", label: "Mouse", imageUrl: "", category: "animals" },
    { id: "lion", label: "Lion", imageUrl: "", category: "animals" },
    { id: "bear", label: "Bear", imageUrl: "", category: "animals" },
    { id: "owl", label: "Owl", imageUrl: "", category: "animals" },
  ],
  vehicles: [
    { id: "car", label: "Car", imageUrl: "", category: "vehicles" },
    { id: "bus", label: "Bus", imageUrl: "", category: "vehicles" },
    { id: "bike", label: "Bike", imageUrl: "", category: "vehicles" },
    { id: "boat", label: "Boat", imageUrl: "", category: "vehicles" },
    { id: "plane", label: "Plane", imageUrl: "", category: "vehicles" },
    { id: "train", label: "Train", imageUrl: "", category: "vehicles" },
    { id: "truck", label: "Truck", imageUrl: "", category: "vehicles" },
    { id: "ship", label: "Ship", imageUrl: "", category: "vehicles" },
  ],
  food: [
    { id: "apple", label: "Apple", imageUrl: "", category: "food" },
    { id: "bread", label: "Bread", imageUrl: "", category: "food" },
    { id: "cheese", label: "Cheese", imageUrl: "", category: "food" },
    { id: "egg", label: "Egg", imageUrl: "", category: "food" },
    { id: "milk", label: "Milk", imageUrl: "", category: "food" },
    { id: "orange", label: "Orange", imageUrl: "", category: "food" },
    { id: "tomato", label: "Tomato", imageUrl: "", category: "food" },
    { id: "cake", label: "Cake", imageUrl: "", category: "food" },
  ],
  household: [
    { id: "chair", label: "Chair", imageUrl: "", category: "household" },
    { id: "lamp", label: "Lamp", imageUrl: "", category: "household" },
    { id: "clock", label: "Clock", imageUrl: "", category: "household" },
    { id: "book", label: "Book", imageUrl: "", category: "household" },
    { id: "key", label: "Key", imageUrl: "", category: "household" },
    { id: "cup", label: "Cup", imageUrl: "", category: "household" },
    { id: "phone", label: "Phone", imageUrl: "", category: "household" },
    { id: "table", label: "Table", imageUrl: "", category: "household" },
  ],
  nature: [
    { id: "tree", label: "Tree", imageUrl: "", category: "nature" },
    { id: "flower", label: "Flower", imageUrl: "", category: "nature" },
    { id: "sun", label: "Sun", imageUrl: "", category: "nature" },
    { id: "moon", label: "Moon", imageUrl: "", category: "nature" },
    { id: "star", label: "Star", imageUrl: "", category: "nature" },
    { id: "cloud", label: "Cloud", imageUrl: "", category: "nature" },
    { id: "leaf", label: "Leaf", imageUrl: "", category: "nature" },
  ],
  faces: [
    { id: "face1", label: "Face", imageUrl: "", category: "faces" },
    { id: "face2", label: "Face", imageUrl: "", category: "faces" },
    { id: "face3", label: "Face", imageUrl: "", category: "faces" },
    { id: "face4", label: "Face", imageUrl: "", category: "faces" },
    { id: "face5", label: "Face", imageUrl: "", category: "faces" },
    { id: "face6", label: "Face", imageUrl: "", category: "faces" },
  ],
};

function getLocalStimulusPool(): NBackStimulusItem[] {
  const out: NBackStimulusItem[] = [];
  for (const arr of Object.values(LOCAL_CATEGORIES)) {
    out.push(...arr);
  }
  return out;
}

const LOCAL_POOL = getLocalStimulusPool();

/**
 * Returns the stimulus pool to use: backend data if valid, otherwise local fallback.
 * Do not block the game on backend availability.
 */
export function getStimulusPool(
  _contentSeed: string,
  _roundIndex: number,
  backendData: unknown,
  backendError: unknown
): NBackStimulusItem[] {
  if (!backendError && isNBackContentResponse(backendData)) {
    const normalized = normalizeNBackContent(backendData);
    if (normalized.length >= 10) return normalized;
  }
  return [...LOCAL_POOL];
}

/** Seeded RNG in [0, 1) for deterministic sequences */
export function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

/**
 * Build a sequence of pool indices of length `sequenceLength` with exactly 30% match positions.
 * Match positions are indices i where sequence[i] === sequence[i - n].
 * Returns { sequence: number[], matchPositions: Set<number> }.
 */
export function buildNBackSequence(
  poolSize: number,
  n: number,
  sequenceLength: number,
  seed: string
): { sequence: number[]; matchPositions: Set<number> } {
  const rng = seededRandom(seed);
  const matchCount = Math.round(sequenceLength * 0.3);
  const matchPositions = new Set<number>();
  const sequence: number[] = new Array(sequenceLength);

  // Choose which indices (>= n) are match positions
  const possibleMatchSlots = Array.from(
    { length: sequenceLength - n },
    (_, i) => i + n
  );
  for (let i = 0; i < matchCount && possibleMatchSlots.length > 0; i++) {
    const j = Math.floor(rng() * possibleMatchSlots.length);
    matchPositions.add(possibleMatchSlots[j]);
    possibleMatchSlots.splice(j, 1);
  }

  for (let i = 0; i < sequenceLength; i++) {
    if (matchPositions.has(i)) {
      sequence[i] = sequence[i - n];
    } else {
      let idx: number;
      do {
        idx = Math.floor(rng() * poolSize);
      } while (n <= i && idx === sequence[i - n]);
      sequence[i] = idx;
    }
  }

  return { sequence, matchPositions };
}

/**
 * Per-round scoring for N-Back.
 *
 * Each trial is one "decision opportunity" (respond vs withhold). Accuracy
 * counts only the `truePositives` against the union of positives
 * (true+false) and missed targets, so a player that never taps gets 0
 * rather than the high score you'd get from counting correct no-go
 * withholdings.
 *
 *  - `matchCount`: number of true matches in the shown sequence.
 *  - `truePositives`: match trials where the player tapped in time.
 *  - `falsePositives`: non-match trials where the player tapped.
 *  - `reactionTimes`: ms-from-onset for each true-positive tap.
 */
export interface NBackRoundInput {
  matchCount: number;
  truePositives: number;
  falsePositives: number;
  reactionTimes: number[];
}

export interface NBackRoundMetrics {
  accuracy: number;
  missed: number;
  total: number;
  errorCount: number;
  avgReactionMs: number;
  reactionTimeMs: number | null;
}

export function computeNBackRoundMetrics(
  input: NBackRoundInput,
): NBackRoundMetrics {
  const { matchCount, truePositives, falsePositives, reactionTimes } = input;
  const missed = Math.max(0, matchCount - truePositives);
  const total = truePositives + falsePositives + missed;
  const accuracy = total > 0 ? truePositives / total : 0;
  const avgReactionMs =
    reactionTimes.length > 0
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
      : 0;
  return {
    accuracy,
    missed,
    total,
    errorCount: falsePositives + missed,
    avgReactionMs,
    reactionTimeMs:
      reactionTimes.length > 0 ? Math.round(avgReactionMs) : null,
  };
}
